import { useEffect, useMemo, useRef, useState } from "react";
import {
  UserRound, X, Camera, Image as ImageIcon, Palette, RotateCcw,
  Sunset, Mountain, Plane, Ship, Compass, Tent,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/* MSP top-bar avatar with 3 ways to personalise:
   1. Camera capture (native `capture` attribute triggers the device camera)
   2. Photo library upload (compressed to 128×128 JPEG client-side)
   3. Themed silhouette picker (beach · mountain · airport · cruise · explorer · camper)

   Stored locally in `localStorage.tp_msp_avatar` for instant offline access,
   and synced to the server via `PUT /api/travel/prefs { msp_avatar }` so it
   follows the traveller across devices when they sign in. */

const STORAGE_KEY = "tp_msp_avatar";
const MAX_SIZE = 128;

const THEMES = [
  { id: "beach",    label: "Beach",    Icon: Sunset,     bg: "bg-amber-100 text-amber-700" },
  { id: "mountain", label: "Mountain", Icon: Mountain,   bg: "bg-emerald-100 text-emerald-700" },
  { id: "airport",  label: "Airport",  Icon: Plane,      bg: "bg-sky-100 text-sky-700" },
  { id: "cruise",   label: "Cruise",   Icon: Ship,       bg: "bg-cyan-100 text-cyan-700" },
  { id: "explorer", label: "Explorer", Icon: Compass,    bg: "bg-fuchsia-100 text-fuchsia-700" },
  { id: "camper",   label: "Camper",   Icon: Tent,       bg: "bg-rose-100 text-rose-700" },
];

function loadStored() {
  try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
}

async function compressToDataUrl(file) {
  const img = document.createElement("img");
  const src = URL.createObjectURL(file);
  try {
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error("Could not read image"));
      img.src = src;
    });
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, MAX_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally { URL.revokeObjectURL(src); }
}

/* Render helper: takes an avatar value (`theme:<id>` | data URL | "") and
   returns a fitting element that fills a circle. */
function AvatarInner({ value, size = "w-5 h-5" }) {
  if (value && value.startsWith("theme:")) {
    const theme = THEMES.find((t) => t.id === value.slice(6));
    if (theme) {
      const Icon = theme.Icon;
      return (
        <div className={`w-full h-full grid place-items-center ${theme.bg}`}>
          <Icon className={size} strokeWidth={1.75} />
        </div>
      );
    }
  }
  if (value && value.startsWith("data:image/")) {
    return <img src={value} alt="Your profile photo" className="w-full h-full object-cover" data-testid="msp-avatar-img" />;
  }
  return <UserRound className={size} strokeWidth={1.75} data-testid="msp-avatar-silhouette" />;
}

export default function MspAvatar() {
  const { user } = useAuth();
  const [value, setValue] = useState(loadStored);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // Sync with server profile: server value wins on first load if it differs.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/travel/prefs").then((r) => {
      if (cancelled) return;
      const remote = r.data?.msp_avatar || "";
      const local = loadStored();
      if (remote && remote !== local) {
        try { localStorage.setItem(STORAGE_KEY, remote); } catch { /* storage full */ }
        setValue(remote);
      } else if (!remote && local) {
        // Push local to server on first sign-in.
        api.put("/travel/prefs", { msp_avatar: local }).catch(() => {});
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Multi-tab sync via storage event.
  useEffect(() => {
    const sync = (e) => { if (e.key === STORAGE_KEY) setValue(e.newValue || ""); };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const save = async (next) => {
    try { next ? localStorage.setItem(STORAGE_KEY, next) : localStorage.removeItem(STORAGE_KEY); }
    catch { /* storage full */ }
    setValue(next);
    if (user) {
      try { await api.put("/travel/prefs", { msp_avatar: next || null }); }
      catch { toast.error("Saved on this device, but couldn't sync — try again later"); return; }
    }
    toast.success(next ? "Profile photo updated" : "Reset to silhouette");
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    try {
      const url = await compressToDataUrl(file);
      await save(url);
      setOpen(false);
    } catch (err) { toast.error(err?.message || "Could not read that image"); }
  };

  const preview = useMemo(() => <AvatarInner value={value} />, [value]);

  return (
    <div className="relative shrink-0">
      <button type="button" onClick={() => setOpen(true)} data-testid="msp-avatar-btn"
        aria-label="Change profile photo" title="Tap to change your profile photo"
        className="w-9 h-9 rounded-full ring-2 ring-blue-100 bg-slate-100 grid place-items-center overflow-hidden text-slate-500 hover:ring-blue-300 transition">
        {preview}
      </button>
      {value && (
        <button type="button" onClick={(e) => { e.stopPropagation(); save(""); }}
          data-testid="msp-avatar-clear" aria-label="Reset profile photo"
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white ring-1 ring-slate-300 grid place-items-center text-slate-500 hover:text-rose-600 hover:ring-rose-300">
          <X className="w-2.5 h-2.5" />
        </button>
      )}

      {/* Hidden file inputs — one for camera, one for library */}
      <input ref={cameraRef} type="file" accept="image/*" capture="user" onChange={handleFile}
        data-testid="msp-avatar-camera-input" className="hidden" />
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
        data-testid="msp-avatar-input" className="hidden" />

      {/* Picker modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-6"
          onClick={() => setOpen(false)} data-testid="msp-avatar-modal">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-5 pb-8 sm:pb-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-serif font-800 text-lg text-slate-900">Choose your avatar</h3>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="msp-avatar-close"
                className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => cameraRef.current?.click()} data-testid="msp-avatar-camera"
                className="flex flex-col items-center gap-1 rounded-2xl border border-slate-200 hover:border-blue-400 py-3 text-slate-700">
                <Camera className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-600">Take photo</span>
              </button>
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="msp-avatar-upload"
                className="flex flex-col items-center gap-1 rounded-2xl border border-slate-200 hover:border-blue-400 py-3 text-slate-700">
                <ImageIcon className="w-5 h-5 text-emerald-600" />
                <span className="text-xs font-600">Upload</span>
              </button>
              <button type="button" onClick={() => save("")} data-testid="msp-avatar-reset"
                className="flex flex-col items-center gap-1 rounded-2xl border border-slate-200 hover:border-blue-400 py-3 text-slate-700">
                <RotateCcw className="w-5 h-5 text-slate-500" />
                <span className="text-xs font-600">Silhouette</span>
              </button>
            </div>

            {/* Themed silhouettes */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-2 flex items-center gap-1.5">
                <Palette className="w-3 h-3" /> Trip-themed silhouettes
              </div>
              <div className="grid grid-cols-3 gap-3" data-testid="msp-avatar-themes">
                {THEMES.map(({ id, label, Icon, bg }) => {
                  const selected = value === `theme:${id}`;
                  return (
                    <button key={id} type="button" onClick={() => { save(`theme:${id}`); setOpen(false); }}
                      data-testid={`msp-avatar-theme-${id}`}
                      className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 py-3 transition ${selected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
                      <div className={`w-11 h-11 rounded-full grid place-items-center ${bg}`}>
                        <Icon className="w-5 h-5" strokeWidth={1.75} />
                      </div>
                      <span className="text-xs font-600 text-slate-700">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {user ? (
              <p className="text-[11px] text-slate-500 text-center pt-1">Synced across your devices while you're signed in.</p>
            ) : (
              <p className="text-[11px] text-slate-500 text-center pt-1">Saved on this device — sign in to sync it across devices.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
